import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useComparison } from "../contexts/ComparisonContext";
import { Scale, X, Check, Minus } from "lucide-react";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
}

function buildGroups(lang) {
  const t = (ro, en) => (lang === "en" ? en : ro);
  return [
    {
      label: t("Informatii generale", "General"),
      rows: [
        { key: "brand",        label: t("Marca",          "Brand"),           kind: "text" },
        { key: "year",         label: t("An fabricatie",  "Year"),             kind: "number", best: "high" },
        { key: "transmission", label: t("Cutie viteze",   "Gearbox"),          kind: "text" },
        { key: "fuel",         label: t("Combustibil",    "Fuel"),             kind: "text" },
      ],
    },
    {
      label: t("Capacitate", "Capacity"),
      rows: [
        { key: "seats",        label: t("Locuri",         "Seats"),            kind: "number", best: "high" },
        { key: "doors",        label: t("Usi",            "Doors"),            kind: "number", best: "high" },
        { key: "luggage_count",label: t("Bagaje",         "Luggage"),          kind: "number", best: "high" },
      ],
    },
    {
      label: t("Motor", "Engine"),
      rows: [
        { key: "engine_cc",    label: t("Capacitate (cc)","Displacement (cc)"),kind: "number", best: "high" },
        { key: "horsepower",   label: t("Cai putere (CP)","Horsepower (HP)"),  kind: "number", best: "high" },
      ],
    },
    {
      label: t("Dotari", "Features"),
      rows: [
        { key: "has_ac",          label: t("Aer conditionat","Air conditioning"), kind: "bool" },
        { key: "has_gps",         label: "GPS",                                    kind: "bool" },
        { key: "has_bluetooth",   label: "Bluetooth",                              kind: "bool" },
        { key: "has_usb",         label: "USB",                                    kind: "bool" },
        { key: "automatic_lights",label: t("Lumini automate","Auto lights"),       kind: "bool" },
      ],
    },
    {
      label: t("Tarife", "Rates"),
      rows: [
        { key: "price_eur",        label: t("Zilnic",      "Daily"),    kind: "price", best: "low" },
        { key: "price_weekly_eur", label: t("Saptamanal",  "Weekly"),   kind: "price", best: "low" },
        { key: "price_monthly_eur",label: t("Lunar",       "Monthly"),  kind: "price", best: "low" },
      ],
    },
  ];
}

function computeBest(cars, rows) {
  const m = {};
  for (const r of rows) {
    if ((r.kind === "number" || r.kind === "price") && r.best && cars.length >= 2) {
      const vals = cars.map((c) => Number(c?.[r.key])).filter((v) => Number.isFinite(v) && v > 0);
      if (vals.length >= 2) m[r.key] = r.best === "high" ? Math.max(...vals) : Math.min(...vals);
    }
  }
  return m;
}

export default function Comparator() {
  const [search, setSearch] = useSearchParams();
  const { lang } = useI18n();
  const { remove } = useComparison();
  const ids = useMemo(() => (search.get("ids") || "").split(",").filter(Boolean).slice(0, 3), [search]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all(ids.map((id) => api.get(`/cars/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setCars(arr.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [ids]);

  const removeCar = (id) => {
    remove(id);
    const next = ids.filter((x) => x !== id);
    if (next.length) setSearch({ ids: next.join(",") });
    else setSearch({});
  };

  const groups = buildGroups(lang);
  const allRows = groups.flatMap((g) => g.rows);
  const bestPerKey = useMemo(() => computeBest(cars, allRows), [cars, allRows]);

  const cols = cars.length;
  const LABEL_W = 152;
  const COL_W = 240;
  const gridCols = `${LABEL_W}px repeat(${cols}, minmax(0, 1fr))`;

  return (
    <div>
      <SeoHead
        title={lang === "en" ? "Compare Cars" : "Comparator Masini"}
        description={lang === "en" ? "Compare rental cars side by side" : "Compara masini de inchiriat"}
      />
      <PublicHeader />

      <main className="pt-28 sm:pt-32 pb-28 min-h-screen" data-testid="comparator-page">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12">

          {/* Page header */}
          <div className="mb-12 sm:mb-16">
            <div className="label-eyebrow mb-3 flex items-center gap-2" style={{ color: "var(--brand-primary)" }}>
              <Scale className="w-3.5 h-3.5" />
              {lang === "en" ? "Side by side" : "Comparatie"}
            </div>
            <h1 className="font-serif font-light tracking-tight" style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}>
              {lang === "en" ? "Compare Cars" : "Compara masini"}
            </h1>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center py-24 gap-4">
              <div className="scroll-line" />
              <span className="label-eyebrow" style={{ color: "var(--brand-text-secondary)" }}>
                {lang === "en" ? "Loading..." : "Se incarca..."}
              </span>
            </div>
          )}

          {/* Empty */}
          {!loading && cars.length === 0 && (
            <div className="flex flex-col items-center text-center py-24 border" style={{ borderColor: "var(--brand-border)" }} data-testid="comparator-empty">
              <Scale className="w-12 h-12 mb-6 opacity-15" />
              <p className="text-sm mb-8 max-w-sm" style={{ color: "var(--brand-text-secondary)" }}>
                {lang === "en"
                  ? "No cars selected. Use the Compare button on any car card to start."
                  : "Nicio masina selectata. Foloseste butonul Compara de pe cardurile masinilor."}
              </p>
              <Link to="/" className="btn-gold">{lang === "en" ? "Browse fleet" : "Vezi flota"}</Link>
            </div>
          )}

          {/* Comparison */}
          {!loading && cars.length > 0 && (
            <div
              className="-mx-4 sm:-mx-6 md:mx-0 px-4 sm:px-6 md:px-0 overflow-x-auto"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div style={{ minWidth: LABEL_W + cols * COL_W + "px" }}>

                {/* ── Car header cards ── */}
                <div className="grid mb-0" style={{ gridTemplateColumns: gridCols, gap: 1, background: "var(--brand-border)" }}>
                  {/* Label spacer */}
                  <div style={{ background: "var(--brand-bg)" }} />

                  {cars.map((car) => (
                    <div key={car.id} style={{ background: "var(--brand-surface)" }} data-testid={`comparator-car-${car.id}`}>
                      {/* Photo */}
                      <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
                        {car.images?.[0] ? (
                          <img src={resolveUrl(car.images[0].url)} alt={car.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full" style={{ background: "var(--brand-surface-elevated)" }} />
                        )}
                        {/* Bottom gradient */}
                        <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: "linear-gradient(transparent, rgba(12,12,12,0.9))" }} />
                        {/* Remove */}
                        <button
                          onClick={() => removeCar(car.id)}
                          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center transition-colors hover:bg-red-600"
                          style={{ background: "rgba(0,0,0,0.75)" }}
                          aria-label={lang === "en" ? "Remove" : "Sterge"}
                          data-testid={`comparator-remove-${car.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Card info */}
                      <div className="px-5 py-4">
                        <Link
                          to={`/cars/${car.id}`}
                          className="font-serif font-light leading-tight block hover:text-[var(--brand-primary)] transition-colors"
                          style={{ fontSize: "1.2rem" }}
                        >
                          {car.name}
                        </Link>
                        <div className="label-eyebrow mt-1 mb-4" style={{ color: "var(--brand-text-secondary)" }}>
                          {car.brand}{car.year ? ` · ${car.year}` : ""}
                        </div>
                        <div className="flex items-baseline gap-1.5 mb-4">
                          <span className="font-serif" style={{ fontSize: "1.85rem", color: "var(--brand-primary)", lineHeight: 1 }}>
                            €{car.price_eur}
                          </span>
                          <span className="label-eyebrow" style={{ color: "var(--brand-text-secondary)" }}>
                            {lang === "en" ? "/day" : "/zi"}
                          </span>
                        </div>
                        <Link
                          to={`/cars/${car.id}`}
                          className="btn-gold-filled block text-center w-full"
                          style={{ fontSize: "0.7rem" }}
                          data-testid={`comparator-book-${car.id}`}
                        >
                          {lang === "en" ? "Reserve" : "Rezerva"}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Spec groups ── */}
                {groups.map((group) => {
                  const visibleRows = group.rows.filter((r) => {
                    if (r.kind === "bool") return true;
                    return cars.some((c) => {
                      const v = c?.[r.key];
                      return v != null && v !== "" && Number(v) !== 0;
                    });
                  });
                  if (!visibleRows.length) return null;

                  return (
                    <div key={group.label} className="mt-10">
                      {/* Group label */}
                      <div className="flex items-center gap-3 mb-0">
                        <div style={{ width: 20, height: 1, background: "var(--brand-primary)", opacity: 0.6 }} />
                        <span className="label-eyebrow" style={{ color: "var(--brand-primary)", opacity: 0.85 }}>
                          {group.label}
                        </span>
                        <div style={{ flex: 1, height: 1, background: "var(--brand-border)" }} />
                      </div>

                      {/* Rows */}
                      <div style={{ border: "1px solid var(--brand-border)", borderTop: "1px solid rgba(212,175,55,0.25)" }}>
                        {visibleRows.map((r, ri) => (
                          <div
                            key={r.key}
                            className="grid"
                            style={{ gridTemplateColumns: gridCols, borderTop: ri > 0 ? "1px solid var(--brand-border)" : "none" }}
                            data-testid={`comparator-row-${r.key}`}
                          >
                            {/* Label cell */}
                            <div
                              className="px-4 py-3.5 flex items-center"
                              style={{ background: "var(--brand-surface)", borderRight: "1px solid var(--brand-border)" }}
                            >
                              <span style={{ fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, color: "var(--brand-text-secondary)" }}>
                                {r.label}
                              </span>
                            </div>

                            {/* Value cells */}
                            {cars.map((car) => {
                              const raw = car?.[r.key];
                              let display = null;
                              let isBest = false;

                              if (r.kind === "bool") {
                                const has = !!raw;
                                isBest = has && cars.some((cc) => !cc[r.key]);
                                display = has
                                  ? <Check className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                                  : <Minus className="w-4 h-4" style={{ opacity: 0.2 }} />;

                              } else if (r.kind === "price") {
                                const num = Number(raw);
                                if (num > 0) {
                                  isBest = bestPerKey[r.key] === num && cars.filter((cc) => Number(cc[r.key]) === num).length === 1;
                                  display = (
                                    <span className="font-serif" style={{ fontSize: "1.1rem" }}>€{num}</span>
                                  );
                                } else {
                                  display = <span style={{ opacity: 0.25 }}>—</span>;
                                }

                              } else if (r.kind === "number") {
                                const num = Number(raw);
                                if (num > 0) {
                                  isBest = bestPerKey[r.key] === num && cars.filter((cc) => Number(cc[r.key]) === num).length === 1;
                                  display = num.toString();
                                } else {
                                  display = <span style={{ opacity: 0.25 }}>—</span>;
                                }

                              } else {
                                display = raw || <span style={{ opacity: 0.25 }}>—</span>;
                              }

                              return (
                                <div
                                  key={car.id}
                                  className="px-4 py-3.5 flex items-center justify-center text-sm"
                                  style={{
                                    borderLeft: "1px solid var(--brand-border)",
                                    background: isBest ? "rgba(212,175,55,0.07)" : "transparent",
                                    color: isBest ? "var(--brand-primary)" : "var(--brand-text)",
                                    fontWeight: isBest ? 600 : 400,
                                    transition: "background 200ms",
                                  }}
                                  data-testid={`comparator-cell-${car.id}-${r.key}`}
                                >
                                  {display}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="mt-8 flex items-center gap-2.5" style={{ color: "var(--brand-text-secondary)", fontSize: "0.7rem", letterSpacing: "0.05em" }}>
                  <div style={{ width: 14, height: 14, background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.35)" }} />
                  {lang === "en" ? "Best value in category" : "Cel mai bun la categorie"}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
